import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';

export default function FinalExamCalculator({ currentPercent }) {
  const [finalWeight, setFinalWeight] = useState('20');
  const [targetGrade, setTargetGrade] = useState('90');
  const [showResult, setShowResult] = useState(false);

  const result = useMemo(() => {
    const C = currentPercent / 100; // Current grade as decimal
    const W = parseFloat(finalWeight) / 100; // Final weight as decimal
    const T = parseFloat(targetGrade) / 100; // Target grade as decimal

    if (isNaN(W) || isNaN(T) || W <= 0 || W > 100) {
      return { valid: false, message: 'Please enter valid percentages' };
    }

    // Formula: Overall = C * (1 - W) + F * W
    // Solve for F: F = (T - C * (1 - W)) / W
    const requiredScore = (T - C * (1 - W)) / W;
    const requiredPercent = requiredScore * 100;

    if (requiredPercent > 100) {
      return {
        valid: true,
        message: `You would need higher than 100% on the final to reach ${targetGrade}%. Your current grade with the final weight considered would give you ${(C * (1 - W) * 100 + 100 * W).toFixed(1)}% max.`,
        score: requiredPercent
      };
    } else if (requiredPercent < 0) {
      return {
        valid: true,
        message: `You can score 0% on the final and still exceed your target of ${targetGrade}%!`,
        score: 0
      };
    } else {
      return {
        valid: true,
        message: `You need at least ${Math.round(requiredPercent)}% on the final to reach ${targetGrade}% overall in this class.`,
        score: requiredPercent
      };
    }
  }, [currentPercent, finalWeight, targetGrade]);

  const handleCalculate = (e) => {
    e.preventDefault();
    setShowResult(true);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Final Exam Calculator</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleCalculate} className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="currentGrade" className="text-sm font-medium">
              Current Grade
            </label>
            <Input
              id="currentGrade"
              type="text"
              value={`${currentPercent.toFixed(1)}%`}
              disabled
              className="bg-muted"
              data-testid="current-grade-input"
            />
          </div>
          <div className="space-y-2">
            <label htmlFor="finalWeight" className="text-sm font-medium">
              Final Exam Weight (%)
            </label>
            <Input
              id="finalWeight"
              type="number"
              step="0.1"
              min="0"
              max="100"
              placeholder="20"
              value={finalWeight}
              onChange={(e) => setFinalWeight(e.target.value)}
              data-testid="final-weight-input"
            />
            <p className="text-xs text-muted-foreground">
              What percentage of your total grade is the final exam?
            </p>
          </div>
          <div className="space-y-2">
            <label htmlFor="targetGrade" className="text-sm font-medium">
              Desired Overall Grade (%)
            </label>
            <Input
              id="targetGrade"
              type="number"
              step="0.1"
              min="0"
              max="100"
              placeholder="90"
              value={targetGrade}
              onChange={(e) => setTargetGrade(e.target.value)}
              data-testid="target-grade-input"
            />
          </div>
          <Button type="submit" className="w-full" data-testid="calculate-button">
            Calculate Required Score
          </Button>
          {showResult && result.valid && (
            <div className="mt-4 p-4 bg-blue-50 rounded-md border border-blue-200" data-testid="required-score-display">
              <p className="text-sm text-[hsl(var(--fg))]">{result.message}</p>
            </div>
          )}
        </form>
      </CardContent>
    </Card>
  );
}